import { createFileRoute } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { Mail, Phone, MapPin } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { LocationMap } from "@/components/contact/location-map";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  head: () => ({
    meta: [
      { title: "Contact — CoderLity" },
      { name: "description", content: "Talk to CoderLity — get a quote within 24 hours. Offices in Riyadh, Jeddah and Rangpur." },
      { property: "og:url", content: "/contact" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/contact" }],
  }),
});

function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState<"paid" | "free">("paid");
  return (
    <PageShell>
      <PageHero eyebrow="Contact" title="Let's build something great" sub="Tell us about your project. We respond within one business day." />
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: Mail, label: "Email", val: "hello@coderlity.com" },
              { icon: Phone, label: "Phone / WhatsApp", val: "+966 55 975 8082" },
              { icon: MapPin, label: "HQ", val: "Riyadh, Saudi Arabia" },
              { icon: MapPin, label: "Branches", val: "Jeddah · CO Bazar, Rangpur" },
            ].map((b) => (
              <div key={b.label} className="rounded-xl border border-border bg-card p-4 flex items-start gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand/15 text-primary ring-1 ring-border">
                  <b.icon className="h-4 w-4" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{b.label}</div>
                  <div className="text-sm font-medium">{b.val}</div>
                </div>
              </div>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setLoading(true);
              setTimeout(() => {
                setLoading(false);
                toast.success("Thanks! We'll get back to you within 24h.");
                (e.target as HTMLFormElement).reset();
                setPricing("paid");
              }, 700);
            }}
            className="lg:col-span-3 relative rounded-3xl border border-border bg-card/80 backdrop-blur p-6 sm:p-8 shadow-card space-y-5 overflow-hidden"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-brand" />
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Start a project</h2>
              <p className="text-xs text-muted-foreground mt-1">Tell us a bit more — we'll reply within 24h.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name" name="name" required />
              <Field label="Email" name="email" type="email" required />
              <Field label="Phone" name="phone" type="tel" placeholder="+966…" />
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Service category</label>
                <select name="service" required defaultValue="" className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary">
                  <option value="" disabled>Select a service</option>
                  <option value="web">Web Development</option>
                  <option value="mobile">Mobile App</option>
                  <option value="design">UI / UX Design</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="hosting">Hosting / Domains</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Engagement</label>
              <div className="mt-2 inline-flex rounded-lg border border-border bg-background p-1 text-sm">
                {(["paid", "free"] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setPricing(opt)}
                    className={`px-4 py-1.5 rounded-md capitalize transition ${pricing === opt ? "bg-gradient-brand text-white shadow-glow" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {opt === "free" ? "Free consult" : "Paid project"}
                  </button>
                ))}
              </div>
              <input type="hidden" name="pricing" value={pricing} />
            </div>
            {pricing === "paid" && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Budget (USD)</label>
                <select name="budget" defaultValue="" className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary">
                  <option value="" disabled>Select a range</option>
                  <option value="<1k">Under $1,000</option>
                  <option value="1k-5k">$1,000 – $5,000</option>
                  <option value="5k-15k">$5,000 – $15,000</option>
                  <option value="15k-50k">$15,000 – $50,000</option>
                  <option value="50k+">$50,000+</option>
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Project brief</label>
              <textarea name="message" required rows={4} className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary resize-none" placeholder="What would you like to build, and any timeline?" />
            </div>
            <button disabled={loading} className="w-full rounded-lg bg-gradient-brand px-4 py-3 text-sm font-semibold text-white shadow-glow disabled:opacity-60 transition hover:-translate-y-0.5">
              {loading ? "Sending..." : "Send message →"}
            </button>
          </form>
        </div>
      </section>
      <section className="pb-16 sm:pb-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="mb-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Our Offices</h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
              CoderLity has <span className="text-primary font-semibold">3</span> offices across{" "}
              <span className="text-primary font-semibold">2</span> countries — pick a branch to see its contact details.
            </p>
          </div>
          <LocationMap />
        </div>
      </section>
    </PageShell>
  );
}

function Field({ label, ...props }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      <input {...props} className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
    </div>
  );
}
